import React, { useState, useEffect } from 'react';
import { updateEmployee } from '../../../../../repository/UserDAO';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import { Select, MenuItem, InputLabel, FormControl, TextField, Button, Grid, InputAdornment } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import BaseModal from '../../../../../components/modals/BaseModal';
import { useOrganizationContext } from '../../../../../context/organization_context/OrganizationContext';
import { Timestamp } from "firebase/firestore";
import { MuiTelInput } from 'mui-tel-input';
import { useToast, ToastType } from '../../../../../context/toast/ToastContext';
import usePermissions from '../../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../../utils/permissions/PermissionTypes';
import AddRoleModal from '../../../Roles/modals/AddRoleModal';
import AddDepartmentModal from '../../Departments/modals/AddDepartmentModal';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';
import { updateSubscribedUsers } from '../../../../../repository/OrganizationDAO';
import { addEmployeeToDepartment } from '../../../../../repository/DepartmentsDAO';

const EditEmployeeModal = ({ show, onHide, employee }) => {
    
    const { setLoadingData } = useLoadingContext();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [birthday, setBirthday] = useState(null);
    const [hireDate, setHireDate] = useState(null);
    const [active, setActive] = useState(true);
    const [terminationDate, setTerminationDate] = useState(null);
    const [employeeRate, setEmployeeRate] = useState('');
    const [employeeTitle, setEmployeeTitle] = useState('');
    const [department, setDepartment] = useState('');
    const [employeeEmail, setEmployeeEmail] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [roleError, setRoleError] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [employeeTitleError, setEmployeeTitleError] = useState(false);
    const [showAddRoleModal, setShowAddRoleModal] = useState(false);
    const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
    const { firebaseUser, userRole } = useUserContext();
    const { organizationDepartments, organizationRoles, addDepartment, addRole } = useOrganizationContext();
    const { showToast } = useToast();
    const{ t } = useTranslation();
    const { hasPermission } = usePermissions();
    const [terminationDateError, setTerminationDateError] = useState(false);

    useEffect(() => {
        if (employee) {
            setFirstName(employee.first_name);
            setLastName(employee.last_name);
            setPhoneNumber(employee.phone_number);
            if (employee.birthday != null)
                setBirthday(dayjs(new Date(employee.birthday.seconds * 1000)));
            if (employee.hire_date != null)
                setHireDate(dayjs(new Date(employee.hire_date.seconds * 1000)));
            setActive(employee.active);
            setEmployeeRate(employee.rate);
            setEmployeeTitle(employee.title);
            setDepartment(employee.department_id);
            setEmployeeEmail(employee.email);
            if (employee.termination_date != null)
                setTerminationDate(dayjs(new Date(employee.termination_date.seconds * 1000)));
            if (employee.role)
                setSelectedRole(employee.role);
        }
    }, [employee, show]);

    const checkValidations = () => {
        let valid = true;
    
        if (!firstName.trim()) {
            setFirstNameError(true);
            valid = false;
        }
    
        if (!lastName.trim()) {
            setLastNameError(true);
            valid = false;
        }
    
        if (!selectedRole.trim()) {
            setRoleError(true);
            valid = false;
        }
    
        if (!phoneNumber.trim()) {
            setPhoneNumberError(true);
            valid = false;
        }
    
        if (!employeeTitle.trim()) {
            setEmployeeTitleError(true);
            valid = false;
        }
    
        if (!active && !terminationDate) {
            setTerminationDateError(true);
            valid = false;
        }
    
        return valid
    }

    const handleSubmit = async () => {
        setLoadingData(true);
        if(!checkValidations()) {
            setLoadingData(false)
            return
        }
    
        const updatedEmployee = {
            first_name: firstName,
            last_name: lastName,
            phone_number: phoneNumber,
            birthday: birthday ? Timestamp.fromDate(new Date(birthday.$d)) : null,
            hire_date: hireDate ? Timestamp.fromDate(new Date(hireDate.$d)) : null,
            rate: employeeRate,
            title: employeeTitle,
            department_id: department,
            email: employeeEmail || null,
            role: selectedRole,
            active: active,
            termination_date: terminationDate ? Timestamp.fromDate(new Date(terminationDate.$d)) : null,
        };
    
        try {
            if (!active && employee.active) {
                await updateSubscribedUsers(firebaseUser.organization, employee.id, false);
            }
            
            var updateEmployeeResult = await updateEmployee(firebaseUser.organization, employee.id, updatedEmployee, showToast);
            var addEmployeeToDeptResult = employee.department_id !== department 
                ? await addEmployeeToDepartment(firebaseUser.organization, employee.department_id, department, employee.id)
                : { success: true };

            if(updateEmployeeResult.success && addEmployeeToDeptResult.success) {
                onHide();
                showToast(t(StringKeys.SUCCESSFULLY_UPDATED_X, {x:`${firstName}`}), ToastType.SUCCESS);
            } else showToast(t(StringKeys.FAILED_TO_UPDATE_X, {x:`${firstName}`}), ToastType.DANGER);
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_UPDATE_X, {x:`${firstName}`}), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    

    const resetForm = () => {
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setBirthday(null);
        setHireDate(null);
        setActive(true);
        setEmployeeRate('');
        setEmployeeTitle('');
        setDepartment('');
        setEmployeeEmail("");
        setFirstNameError(false);
        setLastNameError(false);
        setPhoneNumberError(false);
        setEmployeeTitleError(false);
    };

    const handleActiveChange = (event) => {
        const value = event.target.value === 'true';
        setActive(value);
        if (value) {
            setTerminationDate(null);
        }
    };

    const handleAddRole = async (newRole) => {
        await addRole(newRole, showToast);
        setShowAddRoleModal(false);
        setSelectedRole(newRole);
    };

    const handleAddDepartment = async (newDepartment) => {
        await addDepartment(newDepartment, showToast);
        setShowAddDepartmentModal(false);
        setDepartment(newDepartment);
    };

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    const handlePhoneNumberChange = (newValue) => {
        setPhoneNumber(newValue);
        if (phoneNumberError) setPhoneNumberError(false);
    };

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.EDIT_X, {x:`${employee?.first_name} ${employee?.last_name}`})}
            actions={actions}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TextField
                    margin="dense"
                    id="first-name"
                    label={t(StringKeys.FIRST_NAME)}
                    type="text"
                    fullWidth
                    value={firstName}
                    onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError(false);
                    }}
                    variant="outlined"
                    error={firstNameError}
                    helperText={firstNameError ? t(StringKeys.FIRST_NAME_IS_REQUIRED) : ""}
                />
                <TextField
                    margin="dense"
                    id="last-name"
                    label={t(StringKeys.LAST_NAME)}
                    type="text"
                    fullWidth
                    value={lastName}
                    onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError(false);
                    }}
                    variant="outlined"
                    error={lastNameError}
                    helperText={lastNameError ? t(StringKeys.LAST_NAME_IS_REQUIRED) : ""}
                />
                <MuiTelInput
                    label={t(StringKeys.PHONE)}
                    defaultCountry='US'
                    fullWidth
                    margin="dense"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    error={phoneNumberError}
                    helperText={phoneNumberError ? t(StringKeys.PHONE_NUMBER_IS_REQUIRED) : ""}
                />
                <TextField
                    margin="dense"
                    id="email"
                    label={t(StringKeys.EMAIL)}
                    type="email"
                    disabled={true}
                    fullWidth
                    value={employeeEmail}
                    onChange={(e) => setEmployeeEmail(e.target.value)}
                    variant="outlined"
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel id="department-select-label">{t(StringKeys.DEPARTMENT)}</InputLabel>
                    <Select
                        labelId="department-select-label"
                        id="department-select"
                        value={department}
                        label={t(StringKeys.DEPARTMENT)}
                        onChange={(e) => {
                            if(e.target.value === 'addDepartment') setShowAddDepartmentModal(true)                                
                            else setDepartment(e.target.value)
                        }}
                    >
                        {organizationDepartments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                                {dept.name}
                            </MenuItem>
                        ))}
                        <MenuItem value="addDepartment">
                            <IconPlus /> {t(StringKeys.ADD_DEPARTMENT)}
                        </MenuItem>
                    </Select>
                </FormControl>
                <FormControl
                    error={roleError}
                    fullWidth 
                    margin="dense">
                    <InputLabel id="role-input-label">{t(StringKeys.ROLE)}</InputLabel>
                    <Select
                        error={roleError}
                        label={t(StringKeys.ROLE)}
                        helperText={roleError ? t(StringKeys.EMPLOYEE_ROLE_IS_REQUIRED) : ""}
                        value={selectedRole}
                        disabled={employee?.role === 'Owner'}
                        onChange={(e) => {
                            if(e.target.value === 'addRole') {
                                setShowAddRoleModal(true)
                            } else {
                                setSelectedRole(e.target.value)
                            }
                        }}
                    >
                        {Object.keys(organizationRoles)
                            .map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        {employee?.role !== 'Owner' && (
                            <MenuItem value="addRole">
                                <IconPlus /> {t(StringKeys.ADD_ROLE)}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <DatePicker
                    label={t(StringKeys.HIRE_DATE)}
                    value={hireDate}
                    onChange={(newValue) => setHireDate(newValue)}
                    sx={{ width: "100%", marginTop: "16px", marginBottom: "8px" }}
                />
                <DatePicker
                    label={t(StringKeys.BIRTHDAY)}
                    value={birthday}
                    onChange={(newValue) => setBirthday(newValue)}
                    sx={{ width: "100%", marginTop: "8px", marginBottom: "8px" }}
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel id="active-input-label">{t(StringKeys.ACTIVE)}</InputLabel>
                    <Select
                        labelId="active-select-label"
                        id="active-select"
                        value={active.toString()}
                        label={t(StringKeys.ACTIVE)}
                        onChange={handleActiveChange}
                        variant="outlined"
                    >
                        <MenuItem value={"true"}>{t(StringKeys.YES)}</MenuItem>
                        <MenuItem value={"false"}>{t(StringKeys.NO)}</MenuItem>
                    </Select>
                </FormControl>
                {!active && (
                    <DatePicker
                        label={t(StringKeys.TERMINATION_DATE)}
                        value={terminationDate}
                        onChange={(newValue) => {
                            setTerminationDate(newValue);
                            if (terminationDateError) setTerminationDateError(false);
                        }}
                        sx={{ width: "100%", marginTop: "16px", marginBottom: "8px" }}
                        slotProps={{
                            textField: {
                                error: terminationDateError,
                                helperText: terminationDateError ? t(StringKeys.TERMINATION_DATE_IS_REQUIRED) : ""
                            }
                        }}
                    />
                )}
            </LocalizationProvider>
            <AddRoleModal
                show={showAddRoleModal}
                onHide={() => setShowAddRoleModal(false)}
                onSubmit={handleAddRole}
                shouldRedirect={false}
            />
            <AddDepartmentModal
                show={showAddDepartmentModal}
                onHide={() => setShowAddDepartmentModal(false)}
                onSubmit={handleAddDepartment}
            />
        </BaseModal>
    );
};

export default EditEmployeeModal;
